/**
 * /what-do-you-see — standalone reveal experience.
 * Pulls one random artwork at a time. Visitor interprets → reveals → can hit
 * "Show Me Another" to roll a different one.
 */
import { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { http } from "@/lib/api";
import Layout from "@/components/Layout";
import WhatDoYouSee from "@/components/WhatDoYouSee";
import { ArrowRight, Shuffle, Eye } from "lucide-react";

export default function WhatDoYouSeeGallery() {
  const [art, setArt] = useState(null);
  const [revealCount, setRevealCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(null);

  const roll = useCallback(async (excludeId) => {
    setLoading(true);
    setArt(null);
    try {
      const { data } = await http.get("/reveal/random", { params: excludeId ? { exclude_id: excludeId } : {} });
      setArt(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    http.get("/public/settings").then(({ data }) => {
      setEnabled(data?.reveal_mode_enabled !== false);
    }).catch(() => setEnabled(true));
    roll();
  }, [roll]);

  // Hard-redirect to shop if admin has toggled the feature OFF
  if (enabled === false) return <Navigate to="/shop" replace />;

  return (
    <Layout>
      <section className="bg-[#050505] pt-20 pb-10 border-b border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="font-ui text-[11px] tracking-[0.32em] text-[#C9A055] inline-flex items-center gap-2">
            <Eye size={13}/> WHAT DO YOU SEE?
          </div>
          <h1 className="font-display text-4xl md:text-6xl text-[#F4F1EA] mt-4 leading-[1.05]">
            Look First. Read Last.
          </h1>
          <p className="font-body text-[#B5B0A7] max-w-xl mx-auto mt-6 leading-relaxed">
            A different ordinary thing. A different extraordinary lesson. Tell us what it says to you — then we'll tell you what it says to us.
          </p>
        </div>
      </section>

      <section className="bg-[#050505] py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-5">
          {loading && (
            <div className="border border-white/10 bg-[#0a0a0a] aspect-[4/5] animate-pulse"/>
          )}
          {!loading && !art && (
            <div className="text-center py-16">
              <p className="font-body italic text-[#8a857d]">No artworks available right now.</p>
              <Link to="/shop" className="btn-gold inline-flex items-center gap-2 mt-6">Visit The Studio <ArrowRight size={14}/></Link>
            </div>
          )}
          {art && (
            <WhatDoYouSee
              key={art.id /* remount when we roll a new piece */}
              art={art}
              compact={true}
              onReveal={() => setRevealCount((n) => n + 1)}
            />
          )}

          {/* Roll again */}
          {art && (
            <div className="text-center mt-10 pt-10 border-t border-white/10">
              <button onClick={() => roll(art.id)} className="btn-outline inline-flex items-center gap-2" data-testid="wdys-shuffle-btn">
                <Shuffle size={14}/> Show Me Another
              </button>
              {revealCount > 0 && (
                <p className="font-body text-[11px] text-[#8a857d] mt-3 italic">
                  {revealCount} {revealCount === 1 ? "truth" : "truths"} revealed so far.
                </p>
              )}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
